import React, { useCallback, useEffect, useState } from 'react';
import { useT } from '../i18n/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import DataTable from '../components/ui/DataTable';
import EmptyState from '../components/ui/EmptyState';
import SearchRow, { SearchInput } from '../components/ui/SearchRow';
import Button from '../components/ui/Button';
import { useApi } from '../api/client';
import { listAudit } from '../api/audit';
import useDebounce from '../hooks/useDebounce';
import { formatRelativeTime } from '../utils/format';

const ACTION_TONE = {
  USER_LOGIN: 'open',
  PASSWORD_CHANGED: 'pending',
  MEMBER_CREATED: 'active',
  MEMBER_UPDATED: 'open',
  MEMBER_DELETED: 'completed',
  EVENT_CREATED: 'active',
  EVENT_UPDATED: 'open',
  EVENT_DELETED: 'completed',
  PAYMENT_CREATED: 'active',
  PAYMENT_CONFIRMED: 'paid',
  PAYMENT_UPDATED: 'open',
  PAYMENT_DELETED: 'completed',
  ROLE_CREATED: 'active',
  ROLE_UPDATED: 'open',
  ROLE_DELETED: 'completed',
  USER_CREATED: 'active',
  USER_UPDATED: 'open',
  USER_ACTIVATED: 'active',
  USER_DEACTIVATED: 'inactive',
};

export default function AuditPage() {
  const t = useT();
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [size] = useState(25);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 250);

  const load = useCallback(async () => {
    if (!useApi) return;
    setLoading(true);
    try {
      const data = await listAudit({ q: debouncedQuery, page, size });
      setRows(data?.content || []);
      setTotal(data?.totalElements || 0);
      setTotalPages(data?.totalPages || 0);
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, page, size, toast, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [debouncedQuery]);

  if (!useApi) {
    return (
      <Card title={t('pages.audit.title')} subtitle={t('pages.audit.subtitle')}>
        <EmptyState icon="🔌" title={t('audit_page.api_required')} />
      </Card>
    );
  }

  const columns = [
    { key: 'when', header: t('audit_page.field_when'), render: (r) => formatRelativeTime(r.createdAt, t) },
    { key: 'user', header: t('audit_page.field_user'), render: (r) => r.username || '—' },
    { key: 'action', header: t('audit_page.field_action'),
      render: (r) => <Badge tone={ACTION_TONE[r.action]}>{r.action}</Badge> },
    { key: 'entity', header: t('audit_page.field_entity'),
      render: (r) => r.entityType ? `${r.entityType}${r.entityId ? ' · ' + r.entityId : ''}` : '—' },
    { key: 'description', header: t('audit_page.field_description'), render: (r) => r.description || '—' },
    { key: 'ip', header: t('audit_page.field_ip'), cellClassName: 'mono', render: (r) => r.ipAddress || '—' },
  ];

  return (
    <div>
      <SearchRow>
        <SearchInput value={query} onChange={setQuery} placeholder={t('audit_page.search_placeholder')} />
      </SearchRow>

      <Card title={t('pages.audit.title')} subtitle={t('pages.audit.subtitle')} padded={false}>
        <DataTable
          columns={columns}
          rows={rows}
          empty={<EmptyState title={loading ? t('common.loading') : t('common.no_data')} />}
        />
        {total > 0 && (
          <div className="pagination">
            <div className="results-meta">
              {t('common.showing_results', {
                from: page * size + 1,
                to: Math.min(total, (page + 1) * size),
                total,
              })}
            </div>
            <div className="pagination-controls">
              <Button size="sm" variant="ghost" disabled={page === 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                {t('common.previous')}
              </Button>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {t('common.page_of', { current: page + 1, total: Math.max(1, totalPages) })}
              </span>
              <Button size="sm" variant="ghost" disabled={page >= totalPages - 1 || loading} onClick={() => setPage((p) => p + 1)}>
                {t('common.next')}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
